import React, { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import FileDropComponent from "../components/FileDropComponent";
import config from "../config";
import "./ImpressionHomesContent.css";

const ImpressionHomesContent = () => {
  // Input mode toggles
  const [inputMode, setInputMode] = useState("site"); // "site", "file", or "google_sheet"
  const [skipToStep2, setSkipToStep2] = useState(false);

  // Form state
  const [googleSheetUrl, setGoogleSheetUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Jobs
  const [scrapeJobs, setScrapeJobs] = useState([]);
  const [generateJobs, setGenerateJobs] = useState([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);

  // Track which generate jobs are currently being submitted
  const [submittingGenerate, setSubmittingGenerate] = useState({});

  const fetchJobs = useCallback(async () => {
    try {
      setIsLoadingJobs(true);
      const [scrapeResp, genResp] = await Promise.all([
        fetch(`${config.api.baseUrl}/impression-homes/jobs?type=scrape`, {
          credentials: "include",
        }),
        fetch(`${config.api.baseUrl}/impression-homes/jobs?type=generate`, {
          credentials: "include",
        }),
      ]);
      const scrapeData = await scrapeResp.json();
      const genData = await genResp.json();
      if (scrapeData.success) setScrapeJobs(scrapeData.jobs);
      if (genData.success) setGenerateJobs(genData.jobs);
    } catch (err) {
      console.error("Error fetching jobs:", err);
    } finally {
      setIsLoadingJobs(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Poll while any job is in progress
  useEffect(() => {
    const allJobs = [...scrapeJobs, ...generateJobs];
    const hasRunning = allJobs.some(
      (j) => j.status === "queued" || j.status === "processing"
    );
    if (!hasRunning) return;
    const interval = setInterval(fetchJobs, 3000);
    return () => clearInterval(interval);
  }, [scrapeJobs, generateJobs, fetchJobs]);

  const onFileUpload = (columns, file) => {
    if (file) {
      setSelectedFile(file);
      setFileUploaded(true);
      setError("");
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setFileUploaded(false);
    setGoogleSheetUrl("");
    setError("");
  };

  // --- Step 1: Start Scrape ---
  const handleStartScrape = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      let resp;
      if (inputMode === "site") {
        resp = await fetch(
          `${config.api.baseUrl}/impression-homes/start-scrape`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ site_url: "https://www.impressionhomes.net/" }),
          }
        );
      } else if (inputMode === "file") {
        if (!selectedFile) {
          setError("Please select a file.");
          setIsSubmitting(false);
          return;
        }
        const formData = new FormData();
        formData.append("file", selectedFile);
        resp = await fetch(
          `${config.api.baseUrl}/impression-homes/start-scrape`,
          { method: "POST", credentials: "include", body: formData }
        );
      } else {
        if (!googleSheetUrl.trim()) {
          setError("Please enter a Google Sheet URL.");
          setIsSubmitting(false);
          return;
        }
        resp = await fetch(
          `${config.api.baseUrl}/impression-homes/start-scrape`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ google_sheet_url: googleSheetUrl.trim() }),
          }
        );
      }
      const data = await resp.json();
      if (data.success) {
        resetForm();
        fetchJobs();
      } else {
        setError(data.error || "Failed to start scrape job");
      }
    } catch (err) {
      setError(err.message || "Network error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Step 2: Start Generate (from scrape job) ---
  const handleStartGenerateFromScrape = async (scrapeJobId) => {
    setSubmittingGenerate((prev) => ({ ...prev, [scrapeJobId]: true }));
    try {
      const resp = await fetch(
        `${config.api.baseUrl}/impression-homes/start-generate`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ scrape_job_id: scrapeJobId }),
        }
      );
      const data = await resp.json();
      if (!data.success) {
        alert(data.error || "Failed to start generation");
      }
      fetchJobs();
    } catch (err) {
      alert(err.message || "Network error");
    } finally {
      setSubmittingGenerate((prev) => ({ ...prev, [scrapeJobId]: false }));
    }
  };

  // --- Step 2: Start Generate (direct upload / skip to step 2) ---
  const handleStartGenerateDirect = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      let resp;
      if (inputMode === "file") {
        if (!selectedFile) {
          setError("Please select a file.");
          setIsSubmitting(false);
          return;
        }
        const formData = new FormData();
        formData.append("file", selectedFile);
        resp = await fetch(
          `${config.api.baseUrl}/impression-homes/start-generate`,
          { method: "POST", credentials: "include", body: formData }
        );
      } else {
        if (!googleSheetUrl.trim()) {
          setError("Please enter a Google Sheet URL.");
          setIsSubmitting(false);
          return;
        }
        resp = await fetch(
          `${config.api.baseUrl}/impression-homes/start-generate`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ google_sheet_url: googleSheetUrl.trim() }),
          }
        );
      }
      const data = await resp.json();
      if (data.success) {
        resetForm();
        fetchJobs();
      } else {
        setError(data.error || "Failed to start generation job");
      }
    } catch (err) {
      setError(err.message || "Network error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Helpers ---
  const getStatusBadge = (status) => {
    const map = {
      queued: { label: "Queued", className: "ihc-badge-queued" },
      processing: { label: "Processing", className: "ihc-badge-processing" },
      completed: { label: "Completed", className: "ihc-badge-completed" },
      error: { label: "Error", className: "ihc-badge-error" },
    };
    const info = map[status] || { label: status, className: "" };
    return <span className={`ihc-badge ${info.className}`}>{info.label}</span>;
  };

  const getProgressText = (job) => {
    if (job.status === "completed") return "Done";
    if (job.status === "error") return job.error || "An error occurred";
    const p = job.progress || {};
    if (p.total_rows && p.completed_rows !== undefined) {
      return `${p.current_stage || "Processing"} — ${p.completed_rows}/${p.total_rows} rows`;
    }
    return p.current_stage || "Starting...";
  };

  const getJobLabel = (job) => {
    if (job.input_type === "file_upload") return job.input_filename;
    if (job.source_scrape_job_id) return "From scrape output";
    return "Google Sheet";
  };

  // Check if a scrape job already has a generate job started from it
  const hasGenerateJob = (scrapeJobId) =>
    generateJobs.some((j) => j.source_scrape_job_id === scrapeJobId);

  // --- Render ---
  const renderJobCard = (job, showGenerateButton = false) => (
    <div key={job.job_id} className="ihc-job-card">
      <div className="ihc-job-header">
        <div className="ihc-job-title">{getJobLabel(job)}</div>
        {getStatusBadge(job.status)}
      </div>
      <div className="ihc-job-progress">{getProgressText(job)}</div>
      {job.status === "processing" && job.progress?.total_rows > 0 && (
        <div className="ihc-progress-bar-container">
          <div
            className="ihc-progress-bar"
            style={{
              width: `${Math.round(
                ((job.progress.completed_rows || 0) /
                  job.progress.total_rows) *
                  100
              )}%`,
            }}
          />
        </div>
      )}
      <div className="ihc-job-meta">
        <span>{job.rows_total ? `${job.rows_total} rows` : ""}</span>
        <span>
          {job.created_at ? new Date(job.created_at).toLocaleString() : ""}
        </span>
      </div>
      {job.status === "completed" && job.output_url && (
        <a
          href={job.output_url}
          target="_blank"
          rel="noopener noreferrer"
          className="ihc-output-link"
        >
          Open Output Spreadsheet →
        </a>
      )}
      {job.status === "completed" && job.error_count > 0 && (
        <div className="ihc-warning">
          {job.error_count} row(s) had errors.
        </div>
      )}
      {showGenerateButton &&
        job.status === "completed" &&
        !hasGenerateJob(job.job_id) && (
          <button
            className="ihc-generate-btn"
            disabled={submittingGenerate[job.job_id]}
            onClick={() => handleStartGenerateFromScrape(job.job_id)}
          >
            {submittingGenerate[job.job_id]
              ? "Starting..."
              : "Generate Content →"}
          </button>
        )}
      {showGenerateButton &&
        job.status === "completed" &&
        hasGenerateJob(job.job_id) && (
          <div className="ihc-generate-started">Content generation started</div>
        )}
    </div>
  );

  return (
    <Layout>
      <div className="ihc-container">
        <div className="ihc-header">
          <h1>Impression Homes Content Generator</h1>
          <p className="ihc-subtitle">
            A two-step process: first scrape and extract data from home listing
            pages, then generate AI-powered marketing content from the extracted
            data.
          </p>
        </div>

        {/* Skip-to-step-2 toggle */}
        <div className="ihc-skip-toggle">
          <label className="ihc-switch-label">
            <div
              className={`ihc-switch ${skipToStep2 ? "active" : ""}`}
              onClick={() => {
                setSkipToStep2(!skipToStep2);
                resetForm();
              }}
            >
              <div className="ihc-switch-thumb" />
            </div>
            <span>
              I already have scraped data — skip to Step 2
            </span>
          </label>
        </div>

        {/* Step indicator */}
        <div className="ihc-steps">
          <div
            className={`ihc-step-indicator ${!skipToStep2 ? "active" : "done"}`}
          >
            <div className="ihc-step-number">{skipToStep2 ? "✓" : "1"}</div>
            <div className="ihc-step-label">Scrape & Extract</div>
          </div>
          <div className="ihc-step-connector" />
          <div
            className={`ihc-step-indicator ${skipToStep2 ? "active" : ""}`}
          >
            <div className="ihc-step-number">2</div>
            <div className="ihc-step-label">Generate Content</div>
          </div>
        </div>

        {/* Form */}
        <form
          className="ihc-form"
          onSubmit={skipToStep2 ? handleStartGenerateDirect : handleStartScrape}
        >
          <div className="ihc-form-header">
            <h3>
              {skipToStep2
                ? "Step 2: Upload Scraped Data"
                : "Step 1: Upload Source File"}
            </h3>
            <p className="ihc-form-desc">
              {skipToStep2
                ? "Upload a file or provide a Google Sheet that already has extracted_* columns and a page_type column."
                : "Upload a file or provide a Google Sheet with an \"Address\" column containing URLs to scrape."}
            </p>
          </div>

          <div className="ihc-input-toggle">
            <button
              type="button"
              className={`ihc-toggle-btn ${inputMode === "site" ? "active" : ""}`}
              onClick={() => { setInputMode("site"); setGoogleSheetUrl(""); setSelectedFile(null); setFileUploaded(false); }}
            >
              Crawl from Site
            </button>
            <button
              type="button"
              className={`ihc-toggle-btn ${inputMode === "file" ? "active" : ""}`}
              onClick={() => { setInputMode("file"); setGoogleSheetUrl(""); }}
            >
              Upload File
            </button>
            <button
              type="button"
              className={`ihc-toggle-btn ${inputMode === "google_sheet" ? "active" : ""}`}
              onClick={() => { setInputMode("google_sheet"); setSelectedFile(null); setFileUploaded(false); }}
            >
              Google Sheet URL
            </button>
          </div>

          {inputMode === "site" ? (
            <p className="ihc-site-info">
              Discovers all community, property, and floorplan URLs from the
              Impression Homes sitemap. URLs already in the output sheet will
              be skipped automatically.
            </p>
          ) : inputMode === "file" ? (
            <div className="ihc-file-area">
              <FileDropComponent
                onFileUpload={onFileUpload}
                dataEntered={fileUploaded}
              />
            </div>
          ) : (
            <input
              type="text"
              className="ihc-url-input"
              placeholder="https://docs.google.com/spreadsheets/d/..."
              value={googleSheetUrl}
              onChange={(e) => setGoogleSheetUrl(e.target.value)}
            />
          )}

          {error && <div className="ihc-error">{error}</div>}

          <button
            type="submit"
            className="ihc-submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Starting..."
              : skipToStep2
                ? "Start Content Generation"
                : "Start Scraping"}
          </button>
        </form>

        {/* Scrape Jobs (Step 1) */}
        {!skipToStep2 && (
          <div className="ihc-jobs-section">
            <h2>Step 1 — Scrape Jobs</h2>
            {isLoadingJobs && scrapeJobs.length === 0 && (
              <p className="ihc-loading">Loading...</p>
            )}
            {!isLoadingJobs && scrapeJobs.length === 0 && (
              <p className="ihc-empty">
                No scrape jobs yet. Upload a file to get started.
              </p>
            )}
            {scrapeJobs.length > 0 && (
              <div className="ihc-jobs-list">
                {scrapeJobs.map((job) => renderJobCard(job, true))}
              </div>
            )}
          </div>
        )}

        {/* Generate Jobs (Step 2) */}
        <div className="ihc-jobs-section">
          <h2>Step 2 — Content Generation Jobs</h2>
          {isLoadingJobs && generateJobs.length === 0 && (
            <p className="ihc-loading">Loading...</p>
          )}
          {!isLoadingJobs && generateJobs.length === 0 && (
            <p className="ihc-empty">
              No content generation jobs yet.
              {!skipToStep2 &&
                " Complete a scrape job above, then click \"Generate Content\" to start."}
            </p>
          )}
          {generateJobs.length > 0 && (
            <div className="ihc-jobs-list">
              {generateJobs.map((job) => renderJobCard(job, false))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ImpressionHomesContent;
